import { createFileRoute, Link } from "@tanstack/react-router";
import { EsPageShell, EsSection } from "@/components/site/EsPageShell";
import { CheckpointPhoto } from "@/components/site/CheckpointPhoto";
import { ES_PAGE_MEDIA, procedureImageObjects } from "@/lib/procedure-media";
import { esHead, esWebPageSchema, SITE } from "@/lib/es-pages";

const FAQS: { q: string; a: string }[] = [
  {
    q: "¿El Dr. Fitzer tiene licencia en Carolina del Norte?",
    a: "Sí. Tiene una licencia activa y vigente en el estado.",
  },
  {
    q: "Soy maestro o empleado estatal. ¿Tengo alguna opción?",
    a: "No a través del beneficio de pérdida de peso del State Health Plan, que está cerrado desde abril de 2024 y sigue sin financiamiento. Quedan tres caminos abiertos: los medicamentos GLP-1 siguen cubiertos para la diabetes tipo 2, así que si usted la tiene, esa vía sigue disponible; si tiene apnea obstructiva del sueño, enfermedad cardiovascular establecida o MASH, puede aplicar la vía de indicación alterna; y si el plan del empleador de su cónyuge cubre medicamentos contra la obesidad, ese plan puede ser mejor opción para usted.",
  },
  {
    q: "¿Medicaid de Carolina del Norte cubre Zepbound o Wegovy para bajar de peso?",
    a: "Sí. Desde que se restableció la cobertura el 12 de diciembre de 2025, NC Medicaid vuelve a cubrir los medicamentos GLP-1 para el manejo del peso con autorización previa. Necesitará un IMC documentado que cumpla el umbral de la política, participación en un programa de nutrición y actividad física, y prueba de al menos un 5 por ciento de pérdida de peso para continuar más allá del período de autorización inicial.",
  },
  {
    q: "¿La cirugía bariátrica está cubierta cuando el medicamento no lo está?",
    a: "Con frecuencia sí. La cirugía y el medicamento se rigen por partes completamente separadas de su beneficio, y un plan que excluye los medicamentos contra la obesidad puede seguir cubriendo la cirugía. Los criterios comerciales en Carolina del Norte generalmente exigen un IMC de 40 o más, o de 35 o más con una condición relacionada significativa como diabetes, hipertensión, apnea del sueño o artrosis grave, además de una evaluación psicológica dentro de los 12 meses previos y intentos documentados de manejo de peso bajo supervisión médica.",
  },
  {
    q: "No tengo cobertura para nada. ¿Qué opciones me quedan?",
    a: "Los programas de compra directa del fabricante han bajado bastante los precios de pago propio, y ambos fabricantes principales ahora venden directamente al paciente. Los medicamentos más antiguos que no son GLP-1 son mucho más económicos y funcionan bien para algunas personas. Y vale la pena presentar una apelación cuando la negación fue clínica y no por exclusión de beneficio: una negación por documentación faltante es muy distinta de una exclusión, y solo la primera se puede ganar.",
  },
  {
    q: "¿Puedo recibir tratamiento con Virginia Bariatric Surgery si vivo en Carolina del Norte?",
    a: "Sí. El Dr. Fitzer tiene licencia activa en Carolina del Norte, así que los residentes de ese estado pueden recibir atención médica para bajar de peso, incluida la receta y el apoyo con la autorización previa. Comuníquese a través de nuestra página de contacto y confirmamos qué pide su plan antes de la primera cita.",
  },
];

export const Route = createFileRoute("/es/seguro/carolina-del-norte")({
  head: () => {
    const base = esHead("insuranceNc");
    return {
      ...base,
      scripts: import.meta.env.SSR ? [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": [
              esWebPageSchema("insuranceNc"),
              {
                "@type": "FAQPage",
                "@id": `${SITE}/es/seguro/carolina-del-norte#faq`,
                inLanguage: "es-US",
                mainEntity: FAQS.map((f, i) => ({
                  "@type": "Question",
                  "@id": `${SITE}/es/seguro/carolina-del-norte#faq-${i + 1}`,
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              },
            ],
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@graph": procedureImageObjects(
              ES_PAGE_MEDIA["/es/seguro/carolina-del-norte"],
              "es",
              "https://virginiabariatric.com/es/seguro/carolina-del-norte",
            ),
          }),
        },
      ] : [],
    };
  },
  component: EsNorthCarolinaCoverage,
});

function EsNorthCarolinaCoverage() {
  return (
    <EsPageShell
      page="insuranceNc"
      title="Cobertura de medicamentos para bajar de peso en Carolina del Norte"
      intro="Qué exigen distintas aseguradoras para Zepbound, Wegovy y Saxenda en Carolina del Norte, y la vía alterna que la mayoría de los pacientes nunca conoce."
    >
      <EsSection id="dos-caminos" heading="Cómo funciona la cobertura">
        <p>
          En Carolina del Norte, que su seguro pague o no un medicamento para bajar de peso tiene
          poco que ver con su salud y casi todo que ver con quién redactó su plan. Dos personas con
          el mismo IMC, la misma presión arterial y la misma tarjeta de seguro pueden recibir
          respuestas opuestas, dependiendo de si sus empleadores decidieron incluir el beneficio. Es
          una situación frustrante, pero manejable.
        </p>
        <CheckpointPhoto lang="es" />
        <p>
          Obtener cobertura significa pasar dos puertas de acceso separadas. La primera: ¿su plan
          cubre medicamentos para bajar de peso en absoluto? Es una decisión de diseño del beneficio,
          previa a cualquier evaluación médica. Si su plan excluye los medicamentos contra la
          obesidad como categoría, ningún documento clínico cambiará la respuesta; aquí ocurren la
          mayoría de las negaciones. La segunda: ¿usted cumple los criterios clínicos? Si tiene el
          beneficio, su médico —posiblemente nosotros— presenta una autorización previa mostrando
          que usted cumple los requisitos médicos del plan.
        </p>
        <p>
          El mismo medicamento suele venderse bajo otra marca para otra enfermedad. La semaglutida y
          la tirzepatida tienen una versión aprobada para bajar de peso (Wegovy y Zepbound) y otra
          marca aprobada para la diabetes (Ozempic y Mounjaro). Muchos planes que excluyen la marca
          para bajar de peso sí cubren la de diabetes cuando corresponde clínicamente.
        </p>
      </EsSection>

      <EsSection id="planes" heading="Planes de Carolina del Norte en un vistazo">
        <p>
          Las reglas de cobertura cambian con frecuencia; confirme cualquier dato aquí con los
          documentos de su propio plan.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>
            <strong>NC Medicaid</strong> (todos los planes de atención administrada): cubre desde el
            12 de diciembre de 2025. IMC de 30 o más, o de 27 o más con una condición relacionada;
            adultos de 45 años o más con enfermedad cardiovascular establecida, 27 o más. Exige
            terapia escalonada: Wegovy es preferido, y Zepbound o Saxenda requieren un ensayo
            documentado y falla de Wegovy. La renovación exige un 5 por ciento de pérdida de peso
            mantenida.
          </li>
          <li>
            <strong>NC State Health Plan</strong> (empleados estatales, maestros, jubilados): no
            cubre para bajar de peso. El beneficio se eliminó en abril de 2024 y no se había
            restablecido a mediados de 2026. Los GLP-1 siguen cubiertos para diabetes tipo 2.
          </li>
          <li>
            <strong>Planes de empleadores</strong> (Blue Cross NC, UnitedHealthcare, Aetna, Cigna):
            depende del empleador, no de la aseguradora. Donde hay cobertura, suele exigirse un IMC
            de 30 o más, o de 27 o más con una condición relacionada, y con frecuencia un medicamento
            preferido primero.
          </li>
          <li>
            <strong>Marketplace y grupo pequeño en NC:</strong> generalmente no cubren. La mayoría de
            los formularios del Marketplace de 2026 en Carolina del Norte listan los GLP-1 solo para
            diabetes.
          </li>
          <li>
            <strong>TRICARE</strong> (Prime y Select): cubre según los criterios del formulario de
            TRICARE, con autorización previa requerida.
          </li>
          <li>
            <strong>Medicare</strong> (GLP-1 Bridge, Parte D): cubre desde el 1 de julio de 2026, con
            un copago de $50 por surtido hasta el 31 de diciembre de 2027.
          </li>
        </ul>
        <p>
          Un dato sobre la fila de empleadores: aproximadamente dos tercios de los estadounidenses
          con seguro por su trabajo están en planes autofinanciados, donde el empleador —no la
          aseguradora— decide qué medicamentos cubrir. Por eso dos miembros de Blue Cross NC en
          empresas distintas pueden recibir respuestas opuestas. El nombre de su aseguradora en la
          tarjeta no le dice si el beneficio existe; el formulario de su plan sí.
        </p>
      </EsSection>

      <EsSection id="fechas" heading="Fechas importantes en Carolina del Norte">
        <p>
          Este estado ha tenido más cambios que la mayoría. Si le dijeron que no en los últimos dos
          años, la respuesta pudo haber cambiado.
        </p>
        <ul className="list-disc space-y-2 pl-5">
          <li>Abril de 2024: el State Health Plan dejó de cubrir GLP-1 para bajar de peso.</li>
          <li>1 de agosto de 2024: NC Medicaid empezó a cubrir GLP-1 para el manejo del peso.</li>
          <li>31 de agosto de 2025: TRICARE limitó la elegibilidad a beneficiarios de Prime y Select.</li>
          <li>1 de octubre de 2025: NC Medicaid suspendió la cobertura por falta de fondos estatales; Saxenda se descontinuó por completo.</li>
          <li>12 de diciembre de 2025: NC Medicaid restableció la cobertura con los mismos criterios clínicos vigentes desde agosto de 2024.</li>
          <li>1 de julio de 2026: entró en vigor el Medicare GLP-1 Bridge, con acceso de $50 por surtido hasta el 31 de diciembre de 2027.</li>
        </ul>
      </EsSection>

      <EsSection id="virginia" heading="Diferencias con la cobertura en Virginia">
        <p>
          La mecánica es la misma en ambos estados: su plan cubre el medicamento contra la obesidad
          o no lo cubre, y si lo cubre, la autorización previa decide si usted califica. La
          diferencia está en quién cubre qué. El State Health Plan de Carolina del Norte cerró su
          beneficio de pérdida de peso en abril de 2024 y no lo ha restablecido, mientras que los
          planes de empleados estatales de Virginia siguen cubriendo estos medicamentos con un IMC de
          35 o más. Los planes del Marketplace en ambos estados generalmente excluyen los
          medicamentos para bajar de peso. Vea el detalle completo en nuestra página de{" "}
          <Link to="/es/cobertura-de-seguro" className="font-medium text-brand-logo hover:underline">
            cobertura de seguro
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="alternativas" heading="Si no tiene cobertura">
        <p>
          Cuando el medicamento no está cubierto, vale la pena comparar contra las opciones
          quirúrgicas, que la mayoría de los planes comerciales sí cubren cuando se cumplen los
          criterios de IMC y salud, como la{" "}
          <Link to="/es/manga-gastrica" className="font-medium text-brand-logo hover:underline">
            manga gástrica
          </Link>{" "}
          o el{" "}
          <Link to="/es/bypass-gastrico" className="font-medium text-brand-logo hover:underline">
            bypass gástrico
          </Link>
          . También puede revisar las opciones de{" "}
          <Link
            to="/es/seguro/pago-propio-y-negaciones"
            className="font-medium text-brand-logo hover:underline"
          >
            pago propio y apelación de negaciones
          </Link>
          .
        </p>
      </EsSection>

      <EsSection id="preguntas-frecuentes" heading="Preguntas frecuentes">
        <dl className="space-y-4">
          {FAQS.map((f, i) => (
            <div key={f.q} id={`faq-${i + 1}`}>
              <dt className="font-medium text-brand-deep">{f.q}</dt>
              <dd className="mt-1">{f.a}</dd>
            </div>
          ))}
        </dl>
      </EsSection>
    </EsPageShell>
  );
}
